import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, AlertCircle, AlertTriangle, Award, Check, X, User } from "lucide-react";
import { useSport, SPORT_META } from "@/context/SportContext";

/* -------- Student: claim a prior assessment -------- */
export function ClaimPastAssessment({ onDone }) {
  const { sport } = useSport();
  const meta = SPORT_META[sport] || SPORT_META.pickleball;
  const [dir, setDir] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coachId, setCoachId] = useState("");
  const [priorLevel, setPriorLevel] = useState("3.0");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, m] = await Promise.all([
        api.get(`/coaches/directory?sport=${sport}`),
        api.get("/assessment-claims/mine"),
      ]);
      setDir(d.data);
      setMine(m.data);
    } finally { setLoading(false); }
  }, [sport]);
  useEffect(() => { load(); }, [load]);

  const pendingClaim = mine.find((c) => c.status === "pending" && c.sport === sport);
  const approvedClaim = mine.find((c) => c.status === "approved" && c.sport === sport);

  const submit = async (e) => {
    e.preventDefault();
    if (!coachId) { toast.error("Choose a coach"); return; }
    setSubmitting(true);
    try {
      await api.post("/assessment-claims", {
        coach_id: coachId,
        sport,
        prior_level: parseFloat(priorLevel),
        notes,
      });
      toast.success("Claim submitted — your coach & the admin have been notified");
      setCoachId(""); setPriorLevel("3.0"); setNotes("");
      load(); onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit claim");
    } finally { setSubmitting(false); }
  };

  if (loading) return null;
  if (approvedClaim) return null; // Once approved, hide the card entirely.
  if (pendingClaim) {
    return (
      <div className="card-surface p-5 fade-up d1" data-testid="claim-pending">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" style={{ color: meta.color }} />
          <div className="label-mono">PRIOR {meta.label.toUpperCase()} ASSESSMENT — PENDING</div>
        </div>
        <div className="text-sm text-white mt-2">
          You&apos;ve claimed a prior assessment with <strong>{pendingClaim.coach_name}</strong>. Awaiting their (or an admin&apos;s) approval — we&apos;ll notify you.
        </div>
      </div>
    );
  }

  const selectedCoach = dir.find((c) => c.id === coachId);
  const badReason = selectedCoach && !selectedCoach.is_selectable ? selectedCoach.reason : null;

  return (
    <form onSubmit={submit} className="card-surface p-5 space-y-4 fade-up d1" data-testid="claim-form">
      <div className="flex items-center gap-2">
        <Award className="w-4 h-4" style={{ color: meta.color }} strokeWidth={1.5} />
        <div className="label-mono">ALREADY BEEN ASSESSED?</div>
      </div>
      <div className="text-sm text-[#A3A3A3]">
        If a paid Rally Lab coach has already assessed your {meta.label.toLowerCase()} level, pick them below and we&apos;ll skip the fresh assessment. External / non-Rally-Lab assessments don&apos;t count — you&apos;ll need a Rally Lab session first.
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <div className="label-mono mb-2">Rally Lab coach</div>
          <select
            value={coachId}
            onChange={(e) => setCoachId(e.target.value)}
            data-testid="claim-coach-select"
            className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none"
          >
            <option value="">— Choose a coach —</option>
            {dir.map((c) => (
              <option
                key={c.id}
                value={c.id}
                disabled={!c.is_selectable}
                data-testid={`claim-coach-option-${c.id}`}
              >
                {c.name} {c.is_selectable ? "" : `— ${c.reason}`}
              </option>
            ))}
          </select>
          {badReason && (
            <div
              className="text-[10px] uppercase tracking-wider label-mono mt-1 inline-flex items-center gap-1 text-[#FF4500]"
              data-testid="claim-coach-badge"
            >
              <AlertCircle className="w-3 h-3" /> {badReason}
            </div>
          )}
        </div>
        <div>
          <div className="label-mono mb-2">Assessed level</div>
          <select
            value={priorLevel}
            onChange={(e) => setPriorLevel(e.target.value)}
            data-testid="claim-level"
            className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none"
          >
            {["2.0","2.5","3.0","3.5","4.0","4.5","5.0"].map((l) => <option key={l}>{l}</option>)}
          </select>
        </div>
      </div>

      <div>
        <div className="label-mono mb-2">Notes for the coach (optional)</div>
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid="claim-notes"
          placeholder="e.g. Assessed at Colwyn Bay, Aug 2025"
          className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none"
        />
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting || !selectedCoach || !selectedCoach.is_selectable}
          className="btn-volt py-2.5 px-5 text-xs inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="claim-submit"
        >
          <ShieldCheck className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit claim"}
        </button>
      </div>
    </form>
  );
}

/* -------- Coach / Admin: pending assessment claims -------- */
export function PendingClaimsWidget() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/assessment-claims/pending-for-me");
      setItems(data);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (cid, decision, awarded) => {
    setBusy(cid);
    try {
      await api.post(`/assessment-claims/${cid}/${decision}`, {
        awarded_level: awarded,
        decision_notes: "",
      });
      toast.success(`Claim ${decision}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (loading || !items.length) return null;
  return (
    <div className="card-surface p-5 fade-up d1" data-testid="pending-claims-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="label-mono inline-flex items-center gap-2 text-[#CCFF00]">
          <ShieldCheck className="w-3.5 h-3.5" /> PENDING ASSESSMENT CLAIMS · {items.length}
        </div>
      </div>
      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.id} className="border border-[#262626] p-3 flex items-center gap-3 flex-wrap" data-testid={`pending-claim-${c.id}`}>
            <User className="w-4 h-4 text-[#A3A3A3]" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                {c.student_name}
                {c.prior_claim_count >= 2 && (
                  <PatternFlag studentId={c.student_id} count={c.prior_claim_count} />
                )}
              </div>
              <div className="text-xs text-[#A3A3A3] mt-0.5">
                Claims {c.sport} level {c.prior_level ?? "—"}
                {c.notes ? ` · ${c.notes}` : ""}
              </div>
            </div>
            <button
              type="button"
              onClick={() => act(c.id, "approve", c.prior_level ?? 3.0)}
              disabled={busy === c.id}
              className="text-[10px] uppercase tracking-wider label-mono px-3 py-1.5 border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black inline-flex items-center gap-1 transition-colors disabled:opacity-40"
              data-testid={`approve-${c.id}`}
            >
              <Check className="w-3 h-3" /> Approve
            </button>
            <button
              type="button"
              onClick={() => act(c.id, "reject", null)}
              disabled={busy === c.id}
              className="text-[10px] uppercase tracking-wider label-mono px-3 py-1.5 border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-white inline-flex items-center gap-1 transition-colors disabled:opacity-40"
              data-testid={`reject-${c.id}`}
            >
              <X className="w-3 h-3" /> Reject
            </button>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-[#525252] mt-3 label-mono">
        Prefer to review in detail? <button onClick={() => navigate("/assessment-claims")} className="text-[#A3A3A3] hover:text-[#CCFF00] underline underline-offset-2">Open the full list →</button>
      </div>
    </div>
  );
}

/* Iter90b — amber "seen this before" pill on the pending-claims widget.
   Renders when a student has raised ≥2 prior claims (any status). The
   hover-preview lazy-fetches the full claim history so we don't hammer the
   API for widgets that never get hovered. Falls back gracefully on error. */
function PatternFlag({ studentId, count }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);

  const hydrate = useCallback(async () => {
    if (rows !== null) return;
    try {
      const { data } = await api.get(`/players/${studentId}/claim-history`);
      setRows(Array.isArray(data) ? data.slice(0, 6) : []);
    } catch { setRows([]); }
  }, [rows, studentId]);

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => { setOpen(true); hydrate(); }}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => { setOpen(true); hydrate(); }}
      onBlur={() => setOpen(false)}
    >
      <span
        tabIndex={0}
        data-testid={`claim-pattern-flag-${studentId}`}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider border border-[#FFB020] text-[#FFB020] cursor-help focus:outline-none"
        title={`${count} prior claims — hover to see history`}
      >
        <AlertTriangle className="w-3 h-3" strokeWidth={2} />
        Seen this before · {count}
      </span>
      {open && (
        <span
          className="absolute z-30 top-full left-0 mt-1 w-72 card-surface border border-[#262626] p-3 shadow-lg pointer-events-none"
          data-testid={`claim-pattern-preview-${studentId}`}
        >
          <span className="block label-mono text-[#FFB020] mb-1">PRIOR CLAIMS</span>
          {rows === null ? (
            <span className="block text-xs text-[#525252]">Loading…</span>
          ) : rows.length === 0 ? (
            <span className="block text-xs text-[#525252]">No prior claims found.</span>
          ) : (
            <span className="block space-y-1">
              {rows.map((h) => (
                <span key={h.id} className="block text-[11px] text-[#E5E5E5]">
                  <span className="text-[#525252]">{new Date(h.created_at).toLocaleDateString(undefined,{day:"numeric",month:"short",year:"2-digit"})}</span>
                  {" · "}
                  <span className="capitalize">{h.sport}</span>
                  {" · claimed "}<span className="text-[#CCFF00]">{h.prior_level ?? "?"}</span>
                  {typeof h.awarded_level === "number" && <> → <span style={{color: h.status === "approved" ? "#22C55E" : "#FF4500"}}>{h.awarded_level}</span></>}
                  <span className="text-[#525252]"> · {h.coach_name || "coach"}</span>
                </span>
              ))}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
